import { useState } from 'react';
import Navigation from '../tulote/Navigation';
import Footer from '../tulote/Footer';
import ThemeTogglerTwo from '../tulote/ThemeTogglerTwo';
import LocationPicker from '../tulote/LocationPicker';
import { ThemeProvider } from '../ThemeProvider';
import brandConfig from '../../config/brandConfig';
import {
  ExternalLink, Globe, MapPin, FileText, ArrowRight, AlertCircle,
  MessageCircle, Upload, Send, CheckCircle, Loader2, Clock, ShieldCheck, Zap,
} from 'lucide-react';

const WHY_US = [
  {
    icon: MapPin,
    title: 'Precisión topográfica',
    desc: 'Georreferenciamos tus lotes correctamente para que aparezcan exactos en el mapa.',
  },
  {
    icon: Clock,
    title: 'Te ahorra horas',
    desc: 'Convertir CAD a GeoJSON bien hecho es técnico y lento. Nosotros te lo dejamos listo.',
  },
  {
    icon: Zap,
    title: 'Listo para vender',
    desc: 'Tus lotes en el mapa con su estado (disponible, reservado, vendido), listos para tus clientes.',
  },
  {
    icon: ShieldCheck,
    title: 'Sin riesgo',
    desc: 'La evaluación y la cotización son gratis. Pagas solo si decides avanzar.',
  },
];

const empresas = [
  {
    nombre: "Cloud4Geo",
    url: "https://cloud4geo.com",
    whatsapp: "51949033237",
    descripcion: "Especialistas en soluciones geoespaciales en la nube. Conversión profesional de archivos CAD a GeoJSON con precisión topográfica y digitalización de planos.",
    servicios: ["DWG a GeoJSON", "DXF a GeoJSON", "Shapefile a GeoJSON", "Georreferenciación", "Digitalización de planos"],
    logo: null,
  },
  {
    nombre: "Total Business",
    url: "https://totalbusinesseaae.com",
    whatsapp: "51978858159",
    descripcion: "Soluciones tecnológicas integrales para empresas inmobiliarias. Servicio de conversión.",
    servicios: ["DWG a GeoJSON", "DXF a GeoJSON", "Digitalización de planos", "Soporte técnico"],
    logo: null,
  },
];

const ACCEPTED_EXTENSIONS = ".dwg,.dxf,.zip,.rar,.kml,.kmz,.shp,.geojson,.json,.gpkg,.dgn";
const MAX_FILE_MB = 50;

function ConversionForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', location: '', coordinates: '', message: '' });
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > MAX_FILE_MB * 1024 * 1024) {
      setErrorMsg(`El archivo supera el límite de ${MAX_FILE_MB} MB.`);
      setFile(null);
      e.target.value = '';
      return;
    }
    setErrorMsg('');
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.coordinates) {
      setErrorMsg('Marca la ubicación de tu proyecto en el mapa.');
      return;
    }
    if (!file) {
      setErrorMsg('Selecciona tu archivo CAD.');
      return;
    }
    setStatus('sending');
    setErrorMsg('');

    const data = new FormData();
    data.append('name', form.name);
    data.append('email', form.email);
    data.append('phone', form.phone);
    data.append('location', form.location);
    data.append('coordinates', form.coordinates);
    data.append('source_format', file.name.split('.').pop() || '');
    data.append('message', form.message);
    data.append('file', file);

    try {
      const res = await fetch(`${brandConfig.apiUrl}/conversion-requests/`, {
        method: 'POST',
        body: data,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'No se pudo enviar la solicitud.');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'No se pudo enviar la solicitud. Intenta de nuevo.');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl text-center">
        <div className="w-14 h-14 mx-auto mb-4 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
          <CheckCircle className="w-7 h-7 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          ¡Solicitud recibida!
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Evaluaremos tu archivo y te enviaremos un precio para procesarlo. Revisa tu correo pronto.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="p-6 md:p-8 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre <span className="text-red-500">*</span>
          </label>
          <input
            type="text" name="name" required value={form.name} onChange={handleChange}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            placeholder="Tu nombre"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email" name="email" required value={form.email} onChange={handleChange}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            placeholder="tu@correo.com"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          WhatsApp / Teléfono
        </label>
        <input
          type="tel" name="phone" value={form.phone} onChange={handleChange}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          placeholder="+51 999 999 999"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Ubicación del proyecto <span className="text-red-500">*</span>
        </label>
        <LocationPicker
          onChange={({ location, coordinates }) =>
            setForm((f) => ({ ...f, location, coordinates }))
          }
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Archivo CAD / GIS <span className="text-red-500">*</span>
        </label>
        <label className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
          <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
            {file ? file.name : `DXF (recomendado), DWG, ZIP, KML… (máx. ${MAX_FILE_MB} MB)`}
          </span>
          <input
            type="file" accept={ACCEPTED_EXTENSIONS} onChange={handleFile}
            className="hidden"
          />
        </label>
        <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-lg flex items-start gap-2">
          <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            <strong>Recomendado: DXF ASCII, AutoCAD 2013 o superior.</strong> Es el formato que mejor
            procesamos y el más rápido de convertir. En AutoCAD: <em>Guardar como → DXF</em>. Si solo
            tenés DWG u otro, igual lo intentamos, pero el DXF da mejores resultados.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Mensaje (opcional)
        </label>
        <textarea
          name="message" rows={3} value={form.message} onChange={handleChange}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
          placeholder="Cuéntanos sobre tu proyecto: cantidad de lotes, sistema de coordenadas, etc."
        />
      </div>

      {errorMsg && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {errorMsg}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
      >
        {status === 'sending' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Enviando…
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            Enviar y pedir cotización
          </>
        )}
      </button>
      <p className="mt-3 text-xs text-center text-gray-500 dark:text-gray-400">
        Sin costo por cotizar. Evaluamos tu archivo y te damos un precio antes de procesar.
      </p>
    </form>
  );
}

function ServiciosConversionPage() {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
        <Navigation />

        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-full mb-4">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
                  Conversión CAD a GeoJSON
                </span>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Envíanos tu CAD. Lo evaluamos y te cotizamos.
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Sube tu archivo CAD (DWG, DXF) u otro formato GIS. Lo revisamos, te damos un
                precio para convertirlo a GeoJSON y lo dejamos listo para subir a{' '}
                <strong>{brandConfig.name}</strong>. La evaluación y la cotización son{' '}
                <strong>gratis, sin compromiso</strong>.
              </p>
            </div>

            {/* Como funciona */}
            <div className="mb-10 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                ¿Cómo funciona?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Envía tu archivo</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Sube tu DWG, DXF u otro formato en el formulario</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Lo evaluamos</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Revisamos tu archivo y te damos un precio</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Recibe tu GeoJSON</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Listo para subir a tu proyecto</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Por qué convertir con nosotros */}
            <div className="mb-10">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
                ¿Por qué convertir con nosotros?
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {WHY_US.map(({ icon: Icon, title, desc }) => (
                  <div
                    key={title}
                    className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Formulario */}
            <div className="mb-8">
              <ConversionForm />
            </div>

            {/* Aviso: por qué GeoJSON */}
            <div className="mb-14 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  ¿Por qué GeoJSON?
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {brandConfig.name} trabaja con archivos GeoJSON para representar tus lotes en el mapa de forma precisa.
                  Si tus archivos están en formato CAD, Shapefile u otro formato GIS, necesitas convertirlos a GeoJSON
                  antes de subirlos. Envíanoslos y nosotros hacemos la conversión por ti.
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200 mt-2">
                  Si prefieres hacer la conversión tú mismo, consulta nuestra{' '}
                  <a
                    href="/documentacion"
                    className="font-semibold underline hover:text-blue-600 dark:hover:text-blue-300"
                  >
                    documentación
                  </a>{' '}
                  donde encontrarás guías paso a paso para convertir tus archivos a GeoJSON.
                </p>
              </div>
            </div>

            {/* Empresas aliadas */}
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Empresas aliadas que procesan tu archivo
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Trabajamos con empresas especializadas en conversión geoespacial. También puedes
                contactarlas directamente.
              </p>
            </div>
            <div className="space-y-6">
              {empresas.map((empresa) => (
                <div
                  key={empresa.nombre}
                  className="p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                          <Globe className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            {empresa.nombre}
                          </h3>
                          <a
                            href={empresa.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          >
                            {empresa.url.replace('https://', '')}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {empresa.descripcion}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {empresa.servicios.map((servicio) => (
                          <span
                            key={servicio}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 rounded-full"
                          >
                            <MapPin className="w-3 h-3" />
                            {servicio}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <a
                        href={empresa.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                      >
                        Visitar sitio
                        <ArrowRight className="w-4 h-4" />
                      </a>
                      <a
                        href={`https://wa.me/${empresa.whatsapp}?text=Hola, estoy interesado en el servicio de conversión CAD a GeoJSON`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Contactar especialista
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA para empresas */}
            <div className="mt-12 text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                ¿Tu empresa ofrece conversión de CAD a GeoJSON?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Aparece en este directorio y conecta con usuarios que necesitan tus servicios.
                Contáctanos a{' '}
                <a
                  href="mailto:info@cloud4geo.com?subject=Quiero aparecer en el directorio de conversión CAD a GeoJSON"
                  className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  info@cloud4geo.com
                </a>
              </p>
            </div>
          </div>
        </section>

        <Footer />

        <div className="fixed bottom-6 right-6 z-50 hidden sm:block">
          <ThemeTogglerTwo />
        </div>
      </div>
    </ThemeProvider>
  );
}

export default ServiciosConversionPage;
